import axios from 'axios';

export const HEALTH_STATUS = {
    CHECKING: 'checking',
    ONLINE: 'online',
    DEGRADED: 'degraded',
    CORS: 'cors',
    OFFLINE: 'offline',
};

const API_URL = process.env.REACT_APP_API_URL;
const REQUEST_TIMEOUT = 8000;
const SLOW_RESPONSE_MS = 2500;

// Statuses where an attempted login can only end in a network error.
const BLOCKING_STATUSES = [HEALTH_STATUS.CORS, HEALTH_STATUS.OFFLINE];

export const isLoginBlocked = status => BLOCKING_STATUSES.includes(status);

/**
 * A `no-cors` request is still sent by the browser — only the response is made opaque,
 * and the CORS check is skipped entirely. So if this succeeds while a normal request
 * failed with a network error, the server is up and it's the origin that isn't allowed.
 */
const isReachableWithoutCors = async () => {
    try {
        await fetch(API_URL, { mode: 'no-cors', cache: 'no-store' });
        return true;
    } catch {
        return false;
    }
};

const result = (status, message, extra = {}) => ({
    status,
    message,
    apiUrl: API_URL,
    checkedAt: new Date().toISOString(),
    ...extra,
});

/**
 * Probes the API root endpoint, which reports `{ status, uptime, version, internetConnectivity }`.
 * The server only starts listening once MongoDB is connected, so a healthy root response
 * also means the database is up.
 */
export const checkApiHealth = async () => {
    if (!API_URL) {
        return result(HEALTH_STATUS.OFFLINE, 'No API URL configured (REACT_APP_API_URL is missing)');
    }

    if (navigator.onLine === false) {
        return result(HEALTH_STATUS.OFFLINE, 'This device has no internet connection');
    }

    const startedAt = Date.now();

    try {
        // Deliberately a bare axios call: the shared `api` instance carries interceptors that
        // report failures back to the server and can force a logout — neither belongs in a
        // passive probe running on the login screen.
        const { data } = await axios.get(API_URL, { timeout: REQUEST_TIMEOUT });
        const latency = Date.now() - startedAt;
        const info = {
            latency,
            version: data?.version,
            uptime: data?.uptime,
            internetConnectivity: data?.internetConnectivity,
        };

        if (data?.status !== 'ok') {
            return result(HEALTH_STATUS.DEGRADED, 'The API reported an unhealthy status', info);
        }
        if (data?.internetConnectivity === false) {
            return result(HEALTH_STATUS.DEGRADED, 'The server has lost its internet connection', info);
        }
        if (latency > SLOW_RESPONSE_MS) {
            return result(HEALTH_STATUS.DEGRADED, `The API is responding slowly (${latency} ms)`, info);
        }

        return result(HEALTH_STATUS.ONLINE, 'All systems operational', info);
    } catch (error) {
        const latency = Date.now() - startedAt;

        // A response of any kind means the server is reachable and CORS is fine.
        if (error.response) {
            return result(
                HEALTH_STATUS.DEGRADED,
                `The API answered with an error (HTTP ${error.response.status})`,
                { latency, httpStatus: error.response.status },
            );
        }

        if (error.code === 'ECONNABORTED') {
            return result(
                HEALTH_STATUS.OFFLINE,
                `The API did not answer within ${REQUEST_TIMEOUT / 1000}s`,
                { latency },
            );
        }

        return (await isReachableWithoutCors())
            ? result(
                HEALTH_STATUS.CORS,
                'The server is up but blocks this origin — its CORS configuration needs to allow it',
                { latency },
            )
            : result(HEALTH_STATUS.OFFLINE, 'The API is unreachable', { latency });
    }
};
